import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {ActivatedRouteSnapshot, Resolve, RouterStateSnapshot} from '@angular/router';
import {BehaviorSubject, Observable} from 'rxjs';
import {IcDataService} from '../../services/ic-data.service';
import {NICEFile} from '../../models/NICE_File.model';
import * as JSZip from 'jszip';
import {Buffer} from 'buffer';
import {map} from 'rxjs/operators';
import {GeneralService} from '../../services/general.service';
import {AACFileInfo} from '../../models/AAC_FileInfo';
import {JSON2CSVService} from '../../services/JSON2CSV.service';
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {
    SubmitTranscriptionFilesDialogComponent
} from "../../modals/submit-transcription-files-dialog/submit-transcription-files-dialog.component";
import {SaveFileDialogComponent} from "../../modals/save-file-dialog/save-file-dialog.component";

@Injectable()
export class FileManagerService implements Resolve<any> {
    onFilesChanged: BehaviorSubject<any>;
    onFileSelected: BehaviorSubject<NICEFile>;
    onDirectoryChanged: BehaviorSubject<any>;
    onSearchChanged: BehaviorSubject<any>;
    currentPath = '/';
    submitTranscriptionFilesDialog: MatDialogRef<SubmitTranscriptionFilesDialogComponent, any>;

    /**
     * Constructor
     *
     * @param icData
     * @param _httpClient
     * @param _genHttp
     * @param json2csv
     * @param _matDialog
     */
    constructor(
        private icData: IcDataService,
        private _httpClient: HttpClient,
        private _genHttp: GeneralService,
        private json2csv: JSON2CSVService,
        private _matDialog: MatDialog,
    ) {
        // Set the defaults
        this.onFilesChanged = new BehaviorSubject([]);
        this.onSearchChanged = new BehaviorSubject('');
        this.onFileSelected = new BehaviorSubject(new NICEFile());
        this.onDirectoryChanged = new BehaviorSubject(this.currentPath);
    }

    /**
     * Resolver
     *
     * @param route
     * @param state
     * @returns
     */
    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> | Promise<any> | any {
        //this.refreshDirectory();
    }

    refreshDirectory(path?: string): void {
        let listPath = path;
        if (!listPath) {
            listPath = '/';
        }

        this.icData.aacGetFileList(listPath).then((response: NICEFile[]) => {
            this.onFilesChanged.next(response);
            this.onFileSelected.next(response[0]);
            this.onDirectoryChanged.next(listPath);
            this.currentPath = listPath;
        });
    }

    playAudio(niceFile: NICEFile): void {
        this.icData.getFile(encodeURIComponent(niceFile.fileNameWithPath))
            .subscribe((response: any) => {
                const snd = new Audio('data:audio/wav;base64,' + response.files.file);
                snd.play().then();
            });

    }

    deleteFile(niceFile: NICEFile): Promise<any> {
        const fileNameWithPath = niceFile.fileNameWithPath;
        return new Promise((resolve, reject) => {
            this.icData.deleteFile(fileNameWithPath)
                .then(() => {
                    if (niceFile.aacFileInfo != null && niceFile.aacFileInfo.fileId != null && niceFile.aacFileInfo.fileId !== '') {
                        this.icData.aacDeleteFileInfo(niceFile.aacFileInfo.fileId)
                            .then(() => {
                                resolve(true);
                            }, reject);
                    } else {
                        resolve(false);
                    }
                }, reject);
        });
    }


    deleteFolder(folderPath: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.icData.deleteFolder(folderPath)
                .then(() => {
                    this.icData.aacDeleteFolderInfo(folderPath)
                        .then(() => {
                            resolve(true);
                        }, reject);
                }, reject);
        });
    }

    async renameFile(niceFile: NICEFile, newName: string, overwrite: boolean): Promise<any> {
        newName = newName.replace('//', '/');
        const updatePathResult = await this.icRenameFile(niceFile, newName, overwrite);
        niceFile.aacFileInfo.fileName = newName.substr(newName.lastIndexOf('/') + 1);
        const aacInfoResult = await this.saveFileInfo(niceFile.aacFileInfo);
        return new Promise((resolve, reject) => {
            resolve({updatePathResult, aacInfoResult});
        });
    }

    async icRenameFile(niceFile: NICEFile, newName: string, overwrite: boolean): Promise<any> {
        return new Promise((resolve, reject) => {
            this.icData.updateFilePath(niceFile, newName, overwrite)
                .subscribe((response) => {
                    resolve(response);
                }, reject);
        });
    }

    saveFileInfo(fileInfo: AACFileInfo): Promise<AACFileInfo> {
        return this.icData.aacUpdateFileInfo(fileInfo);
    }


    uploadFile(fileNameWithPath: string, base64File: string, overwrite: boolean, fileTranscription?: string, voiceId?: string): Promise<any>
    {
        const uploadFile = {
            fileName: fileNameWithPath,
            file: base64File,
            overwrite: overwrite,
            fileTranscription: fileTranscription,
            voiceId: voiceId
        };
        return new Promise((resolve, reject) => {
            this.icData.uploadFile(uploadFile)
                .then((response) => {
                    resolve(response);
                }, reject);
        });
    }

    downloadFile(niceFile: NICEFile): void{
        this.icData.getFile(encodeURIComponent(niceFile.fileNameWithPath))
            .subscribe((response: any) => {
                const blob = this.base64ToBlob(response.files.file, 'application/octet-stream');
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = niceFile.fileName;
                link.click();
            });

    }

    downloadTranscriptions(): void{
        this.icData.aacBUTranscriptions()
            .then((response: any) => {
                const opts = {
                    fields: [
                        {
                            label: 'filePath',
                            value: row => row.filePath + '/' + row.fileName
                        },
                        {
                            label: 'promptMessage',
                            value: 'fileTranscription'
                        },
                        {
                            label: 'voiceId',
                            value: 'voiceId'
                        }
                        ]
                };
                this.json2csv.downloadFile( this.json2csv.convertToCSV(response, opts), 'Transcriptions', );
            });


    }

    async getFolderFiles(niceFiles: NICEFile[]): Promise <NICEFile[]>
    {
        const files: NICEFile[] = [];
        for (const nicefile of niceFiles) {
            if (!nicefile.isFolder) {
                files.push(nicefile);
            }
            else {
                const fileData: NICEFile[] = await this.icData.aacGetFileList(nicefile.location);

                for (const file of fileData) {
                    for (const subfile of await this.getFolderFiles([file])) {
                        files.push(subfile);
                    }
                }
            }

        }
        return files;
    }

    async submitTranscriptions( niceFiles: NICEFile[]): Promise <any> {
        this.submitTranscriptionFilesDialog = this._matDialog.open(SubmitTranscriptionFilesDialogComponent, {
            disableClose: true,
            panelClass: 'submit-transcription-dialog-panel',
            data: {
                records: niceFiles,
            }
        });
        this.submitTranscriptionFilesDialog.afterClosed().subscribe((result) => {
            if (result) {
                this.refreshDirectory(this.currentPath);
            }
        });
    }

    async downloadFiles(niceFiles: NICEFile[], zipName?: string): Promise <any> {
        const zip = new JSZip();
        const name = zipName + '.zip';
        for (const element of niceFiles) {
            const fileData: any = await this.getFile(element);
            const blob = this.base64ToBlob(fileData.files.file, 'application/octet-stream');
            if (this.currentPath === '/')
            {
                zip.file( element.fileNameWithPath.substr(element.fileNameWithPath.indexOf(this.currentPath) + this.currentPath.length), blob);
            }
            else {
                zip.file( element.fileNameWithPath.substr(element.fileNameWithPath.indexOf(this.currentPath) + this.currentPath.length + 1), blob);
            }
        }
        zip.generateAsync({ type: 'blob' }).then((content) => {
            if (content) {
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(content);
                link.download = zipName;
                link.click();
                // FileSaver.saveAs(content, name);
            }
        });
    }

    async getFile(niceFile: NICEFile): Promise<any> {
        return await this.icData.getFile(encodeURIComponent(niceFile.fileNameWithPath)).toPromise().catch((err: HttpErrorResponse) => err.error);
    }


    public base64ToBlob(b64Data, contentType= '', sliceSize= 512): Blob{
        b64Data = b64Data.replace(/\s/g, '');
        const byteCharacters = atob(b64Data);
        const byteArrays = [];
        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);

            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }
        return new Blob(byteArrays, {type: contentType});
    }
    isulaw(fileData: string): boolean {
        // this a list of sequenced bytes in the 40 byte header. This builds the readResult object.
        //  Property name / Data type / Length

        const reads = [
            ['riff_head', 'string', 0, 4],
            ['chunk_size', 'uinteger', 4, 8],
            ['wave_identifier', 'string', 8, 12],
            ['fmt_identifier', 'string', 12, 16],
            ['subchunk_size', 'integer', 16, 20],
            ['audio_format', 'integer', 20, 22],
            ['num_channels', 'integer', 22, 24],
            ['sample_rate', 'integer', 24, 28],
            ['byte_rate', 'integer', 28, 32],
            ['block_align', 'integer', 32, 34],
            ['bits_per_sample', 'integer', 34, 36],
            ['data_identifier', 'string', 36, 40],
            ['sub_chunk2_size', 'integer', 40, 44],

        ];

        const readResult = {};

        // const buffer = new Buffer(40) ;
        const buffer = Buffer.from(fileData, 'ascii');
        for (const read of reads) {
            if (read[1] === 'string') {
                readResult[read[0]] = buffer.toString('ascii', +read[2], +read[3]);
            } else if (read[1] === 'integer') {

                readResult[read[0]] = buffer.readUInt16LE(+read[2]);
            } else if (read[1] === 'uinteger') {

                readResult[read[0]] = buffer.readInt32LE(+read[2]);
            }
        }
        return readResult['audio_format'] === 7;
    }
    public convertFile(fileType: string, base64File: string): Promise<any> {
        //  curl -i -H "Authorization: Basic c2t5Ym94TGljZW5zZTpza3liMHhQNCQk" -H "Content-Type:application/json"
        //  -X POST -d "{\"fileType\":\"mp3\",\"file\":\"BASE64encodedfile\"}" http://localhost:8080/skyboxaaclicense/registeredBusinessunit/convertAudioFileToUlaw/
        const postData = {
            fileType: fileType,
            file: base64File
        };
        return new Promise((resolve, reject) => {
            const URL = 'awsURL/convertAudioFileToUlaw/';
            this._genHttp.icPost(URL, JSON.stringify(postData))
                .pipe(map((response: any) => response))
                .subscribe((response: any) => {
                    resolve(response);
                }, reject);
        });
    }


    getFileFromAsset(fileName){
        return this._genHttp.textGet('assets/' + fileName)
    }

    // Drop handler function to get all files
    async getAllFileEntries(dataTransferItemList): Promise<any[]> {
        const fileEntries = [];
        // Use BFS to traverse entire directory/file structure
        const queue = [];
        // Unfortunately dataTransferItemList is not iterable i.e. no forEach
        for (const item of dataTransferItemList) {
            queue.push(item.webkitGetAsEntry());
        }
        while (queue.length > 0) {
            const entry = queue.shift();
            if (entry.isFile) {
                const file = await this.getFileFromEntry(entry);
                file.fullPath = entry.fullPath;
                fileEntries.push(file);
            } else if (entry.isDirectory) {
                queue.push(...await this.readAllDirectoryEntries(entry.createReader()));
            }
        }
        return fileEntries;
    }
    async getFileFromEntry(fileEntry: any): Promise<any> {
        try {
            return await new Promise((resolve, reject) => fileEntry.file(resolve, reject));
        } catch (err) {
            console.log(err);
        }
    }
    // Get all the entries (files or subdirectories) in a directory
    // by calling readEntries until it returns empty array
    async readAllDirectoryEntries(directoryReader): Promise<any[]> {
        const entries = [];
        let readEntries = await this.readEntriesPromise(directoryReader);
        while (readEntries.length > 0) {
            entries.push(...readEntries);
            readEntries = await this.readEntriesPromise(directoryReader);
        }
        return entries;
    }

    // Wrap readEntries in a promise to make working with readEntries easier
    // readEntries will return only some of the entries in a directory
    // e.g. Chrome returns at most 100 entries at a time
    async readEntriesPromise(directoryReader): Promise<any> {
        try {
            return await new Promise((resolve, reject) => {
                directoryReader.readEntries(resolve, reject);
            });
        } catch (err) {
            console.log(err);
        }
    }


}
